// Methods not using "this" are implementing the interface and cannot be static
/* eslint-disable class-methods-use-this */

import { AxiosResponse } from 'axios';
import { DormantAccountSettings } from './DormantAccountSettings';
import { AccountDormantByStatuses } from './AccountDormantByStatuses';
import VersionedResource from '../VersionedResource';
import { StatusSettingsResource } from './StatusSettingsResource';
import { StatusSettings } from './StatusSettings';

export class DormantAccountSettingsResource extends VersionedResource implements StatusSettingsResource {
  public async getOne(fiId: string): Promise<DormantAccountSettings> {
    const response: AxiosResponse = await this.apiClient.get(`/api/rcx/config/${fiId}/dormant-account-settings`);
    const settings: DormantAccountSettings = response.data.data;
    return settings;
  }

  public async save(fiId: string, settings: DormantAccountSettings): Promise<DormantAccountSettings> {
    const toSave: any = { };
    Object.assign(toSave, settings);

    // Application logic should prevent UNSET value from reaching here, as it's only meaningful on the
    // client side.  However, in case of a logic error, that value should not be passed to the server.
    if (<unknown>settings.accountDormantBy === AccountDormantByStatuses.UNSET) {
      toSave.accountDormantBy = undefined;
    } else if (<unknown>settings.accountDormantBy === AccountDormantByStatuses.DORMANT_DATE) {
      toSave.statusCodes = [];
    }

    const response: AxiosResponse = await
    this.apiClient.put(`/api/rcx/config/${fiId}/dormant-account-settings/`, toSave);

    const savedSetting: DormantAccountSettings = response.data.data;
    return savedSetting;
  }

  createEmptySettings(): DormantAccountSettings {
    return {
      accountDormantBy: AccountDormantByStatuses.UNSET,
      statusCodes: [],
      active: true,
    };
  }

  setIndicatorsOnSettings(settings: StatusSettings, values: string[], conditionalOr: boolean | null) {
    const dormantSettings = settings as DormantAccountSettings;
    if (values.length === 2) {
      if (conditionalOr) {
        dormantSettings.accountDormantBy = AccountDormantByStatuses.STATUS_CODE_OR_DORMANT_DATE;
      } else {
        dormantSettings.accountDormantBy = AccountDormantByStatuses.STATUS_CODE_AND_DORMANT_DATE;
      }
    } else if (values.length === 0) {
      dormantSettings.accountDormantBy = AccountDormantByStatuses.UNSET;
    } else if (values[0] === 'DORMANT_DATE') {
      dormantSettings.accountDormantBy = AccountDormantByStatuses.DORMANT_DATE;
    } else if (values[0] === 'STATUS_CODE') {
      dormantSettings.accountDormantBy = AccountDormantByStatuses.STATUS_CODE;
    }
  }

  getIndicatorsFromSettings(settings: StatusSettings): string[] | null {
    const dormantSettings = settings as DormantAccountSettings;
    let indicators: string[] = [];
    switch (dormantSettings.accountDormantBy) {
      case AccountDormantByStatuses.DORMANT_DATE:
        indicators = [AccountDormantByStatuses.DORMANT_DATE];
        break;


      case AccountDormantByStatuses.STATUS_CODE:
        indicators = [AccountDormantByStatuses.STATUS_CODE];
        break;

      case AccountDormantByStatuses.STATUS_CODE_OR_DORMANT_DATE:
      case AccountDormantByStatuses.STATUS_CODE_AND_DORMANT_DATE:
        indicators = [AccountDormantByStatuses.STATUS_CODE, AccountDormantByStatuses.DORMANT_DATE];
        break;

      default:
        break;
    }

    return indicators;
  }

  getConditionalFromSettings(settings: StatusSettings): string | null {
    const dormantSettings = settings as DormantAccountSettings;

    let conditional: string | null = null;
    switch (dormantSettings.accountDormantBy) {
      case AccountDormantByStatuses.STATUS_CODE_OR_DORMANT_DATE:
        conditional = 'OR';
        break;

      case AccountDormantByStatuses.STATUS_CODE_AND_DORMANT_DATE:
        conditional = 'AND';
        break;

      default:
        break;
    }

    return conditional;
  }
}

export default DormantAccountSettingsResource;
